import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

export type ApiClientConfig = {
  baseURL: string;
  getAccessToken?: () => Promise<string | null> | string | null;
  onUnauthorized?: () => void;
};

let configuredInstance: AxiosInstance | null = null;

/**
 * Configure the singleton axios instance the orval-generated mutator uses.
 * Call once during app bootstrap, before any generated hook fires.
 */
export function configureApiClient(config: ApiClientConfig): void {
  const instance = axios.create({ baseURL: config.baseURL });

  instance.interceptors.request.use(async (request) => {
    const token = await (typeof config.getAccessToken === 'function'
      ? config.getAccessToken()
      : null);
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: { status?: number } }).response?.status === 'number' &&
        (error as { response?: { status: number } }).response!.status === 401 &&
        config.onUnauthorized
      ) {
        config.onUnauthorized();
      }
      return Promise.reject(error);
    },
  );

  configuredInstance = instance;
}

/**
 * Mutator referenced by orval.config.ts. Generated hooks call this for every
 * request, so it must be configured before the first hook resolves.
 */
export async function apiClient<TData>(
  request: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<TData> {
  if (!configuredInstance) {
    throw new Error('@iplan/api-client: configureApiClient() must be called during app bootstrap.');
  }
  const merged: AxiosRequestConfig = { ...request, ...options };
  const response = await configuredInstance.request<TData>(merged);
  return response.data;
}
